import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  Clock,
  CheckCircle2,
  XCircle,
  Database,
  ArrowLeft,
  History,
  Activity,
  Bot,
  Globe,
  ScrollText,
  Sparkles,
} from "lucide-react";
import { AdminLayout } from "./AdminLayout";
import { adminListOpportunities, adminListSources } from "@/services/adminService";
import { ADMIN } from "@/constants/testIds";
import { useAuth } from "@/context/AuthContext";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

const KpiCard = ({ testId, label, value, loading, Icon, accent, to }) => {
  const Wrapper = to ? Link : "div";
  const props = to ? { to } : {};
  return (
    <Wrapper
      {...props}
      data-testid={testId}
      className="group relative overflow-hidden rounded-xl border border-surface-200 bg-white p-5 transition-colors hover:border-intel-500"
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs font-medium text-ink-500">{label}</div>
          {loading ? (
            <div
              data-testid={`${testId}-skeleton`}
              aria-busy="true"
              aria-label="جاري التحميل"
              className="mt-3 h-9 w-16 animate-pulse rounded-md bg-surface-100"
            />
          ) : (
            <div
              data-testid={`${testId}-value`}
              className="mt-3 font-mono text-3xl font-semibold text-ink-900"
            >
              {value}
            </div>
          )}
        </div>
        <span
          aria-hidden
          className={`inline-flex h-10 w-10 items-center justify-center rounded-lg ${accent}`}
        >
          <Icon className="h-5 w-5" strokeWidth={1.75} />
        </span>
      </div>
      {to && (
        <div className="mt-4 inline-flex items-center gap-1 text-xs font-medium text-intel-800">
          <span>عرض التفاصيل</span>
          <ArrowLeft className="h-3.5 w-3.5 transition-transform group-hover:-translate-x-0.5" strokeWidth={1.75} />
        </div>
      )}
    </Wrapper>
  );
};

const StatusPill = ({ testId, Icon, label, value, tone }) => {
  const tones = {
    ok:      "border-emerald-200 bg-emerald-50 text-emerald-800",
    warn:    "border-amber-200 bg-amber-50 text-amber-800",
    neutral: "border-surface-200 bg-white text-ink-700",
    proof:   "border-intel-200 bg-intel-50 text-intel-800",
  };
  return (
    <div
      data-testid={testId}
      className={`flex items-center gap-3 rounded-xl border p-4 ${tones[tone] || tones.neutral}`}
    >
      <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-white/70">
        <Icon className="h-4 w-4" strokeWidth={1.75} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-[11px] font-semibold uppercase tracking-widest opacity-70">{label}</div>
        <div className="mt-0.5 truncate text-sm font-semibold">{value}</div>
      </div>
    </div>
  );
};

const fmtTs = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("ar", { dateStyle: "short", timeStyle: "short" });
  } catch { return "—"; }
};

export default function AdminDashboardPage() {
  const [kpi, setKpi] = useState({
    signals_ingested: 0, signals_analyzed: 0, in_review: 0,
    published: 0, auto_published: 0, rejected: 0,
    sources_active: 0, sources_ae: 0,
  });
  const [automation, setAutomation] = useState({ enabled: false, running: false, last_tick: null });
  const [market, setMarket] = useState("proof");
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    let cancelled = false;
    const tasks = [
      adminListOpportunities().catch(() => []),
      adminListSources().catch(() => []),
      fetch(`${API_BASE}/api/stats/public`).then((r) => r.ok ? r.json() : {}).catch(() => ({})),
      fetch(`${API_BASE}/api/admin/automation/state`, { credentials: "include" })
        .then((r) => r.ok ? r.json() : {}).catch(() => ({})),
      fetch(`${API_BASE}/api/settings/public`)
        .then((r) => r.ok ? r.json() : {}).catch(() => ({})),
    ];
    Promise.all(tasks)
      .then(([rows, sources, stats, autoState, settings]) => {
        if (cancelled) return;
        const byStatus = (rows || []).reduce(
          (acc, r) => ({ ...acc, [r.status]: (acc[r.status] ?? 0) + 1 }),
          { pending: 0, published: 0, rejected: 0 },
        );
        setKpi({
          signals_ingested:  stats.signals_ingested ?? 0,
          signals_analyzed:  stats.signals_analyzed ?? 0,
          in_review:         stats.opportunities_in_review ?? byStatus.pending,
          published:         stats.opportunities_discovered ?? byStatus.published,
          auto_published:    stats.opportunities_auto_published ?? 0,
          rejected:          byStatus.rejected,
          sources_active:    (sources || []).filter((s) => s.isActive).length,
          sources_ae:        (sources || []).filter((s) => s.isActive && String(s.region || "").includes("الإمارات")).length,
        });
        setAutomation({
          enabled: !!autoState.enabled,
          running: !!autoState.running,
          last_tick: autoState.last_tick_finished_at || autoState.last_tick_started_at || null,
          last_error: autoState.last_error || null,
        });
        setMarket(settings.market_ae_status || "proof");
        setLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setLoading(false);
        toast.error("تعذّر تحميل مؤشرات الأداء", { description: String(e) });
      });
    return () => { cancelled = true; };
  }, [user?.id]);

  const automationLabel = !automation.enabled
    ? "متوقّف"
    : automation.running
      ? `قيد التشغيل · آخر تنبيه ${fmtTs(automation.last_tick)}`
      : "مُفعّل — لم يعمل بعد";
  const automationTone = !automation.enabled
    ? "neutral"
    : automation.last_error
      ? "warn"
      : automation.running ? "ok" : "neutral";

  const marketLabels = {
    proof: "وضع الإثبات",
    active: "نشط",
    coming_soon: "قريباً",
    disabled: "معطّل",
  };

  return (
    <AdminLayout>
      <section className="space-y-8">
        <header className="space-y-2">
          <p className="text-xs font-medium uppercase tracking-widest text-intel-800">
            نظرة عامة
          </p>
          <h2 className="text-2xl font-semibold tracking-tight text-ink-900 sm:text-3xl">
            مركز القيادة
          </h2>
          <p className="max-w-2xl text-sm leading-relaxed text-ink-500">
            نبضات المنصّة الحيّة — الاستلام، التحليل، النشر، وحالة الأتمتة والسوق.
          </p>
        </header>

        {/* Automation + Market pills */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <StatusPill
            testId="admin-pill-automation"
            Icon={Bot}
            label="الأتمتة"
            value={automationLabel}
            tone={automationTone}
          />
          <StatusPill
            testId="admin-pill-market-ae"
            Icon={Globe}
            label="سوق الإمارات"
            value={marketLabels[market] || market}
            tone={market === "active" ? "ok" : market === "proof" ? "proof" : "neutral"}
          />
        </div>

        {/* KPI grid — 8 tiles */}
        <div
          data-testid={ADMIN.kpiGrid}
          className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
        >
          <KpiCard
            testId="admin-kpi-signals-ingested"
            label="إشارات مُستلمة"
            value={kpi.signals_ingested}
            loading={loading}
            Icon={ScrollText}
            accent="bg-slate-50 text-slate-700"
            to="/admin/feed"
          />
          <KpiCard
            testId="admin-kpi-signals-analyzed"
            label="إشارات مُحلَّلة"
            value={kpi.signals_analyzed}
            loading={loading}
            Icon={Activity}
            accent="bg-intel-50 text-intel-800"
            to="/admin/ai-queue"
          />
          <KpiCard
            testId={ADMIN.kpiPending}
            label="قيد المراجعة"
            value={kpi.in_review}
            loading={loading}
            Icon={Clock}
            accent="bg-amber-50 text-amber-700"
            to="/admin/review"
          />
          <KpiCard
            testId={ADMIN.kpiPublished}
            label="فرص مَنشورة"
            value={kpi.published}
            loading={loading}
            Icon={CheckCircle2}
            accent="bg-emerald-50 text-emerald-700"
            to="/admin/review?status=published"
          />
          <KpiCard
            testId="admin-kpi-auto-published"
            label="نُشرت تلقائياً"
            value={kpi.auto_published}
            loading={loading}
            Icon={Sparkles}
            accent="bg-fursa-50 text-fursa-800"
          />
          <KpiCard
            testId={ADMIN.kpiRejected}
            label="مرفوضة"
            value={kpi.rejected}
            loading={loading}
            Icon={XCircle}
            accent="bg-red-50 text-red-700"
            to="/admin/review?status=rejected"
          />
          <KpiCard
            testId={ADMIN.kpiSources}
            label="مصادر نَشِطة"
            value={kpi.sources_active}
            loading={loading}
            Icon={Database}
            accent="bg-intel-50 text-intel-800"
            to="/admin/sources"
          />
          <KpiCard
            testId="admin-kpi-sources-ae"
            label="مصادر إماراتيّة"
            value={kpi.sources_ae}
            loading={loading}
            Icon={Globe}
            accent="bg-emerald-50 text-emerald-700"
            to="/admin/sources"
          />
        </div>

        <div className="rounded-xl border border-surface-200 bg-white">
          <div className="flex items-center justify-between border-b border-surface-200 px-5 py-4">
            <h3
              data-testid={ADMIN.activityTitle}
              className="text-sm font-semibold text-ink-900"
            >
              آخر النشاطات
            </h3>
            <span className="text-xs text-ink-500">
              {automation.last_tick ? `آخر تنبيه أتمتة: ${fmtTs(automation.last_tick)}` : "آخر 24 ساعة"}
            </span>
          </div>
          <div
            data-testid={ADMIN.activityList}
            className="flex flex-col items-center justify-center gap-3 px-6 py-14 text-center"
          >
            <div className="flex h-11 w-11 items-center justify-center rounded-full bg-surface-50 ring-1 ring-surface-200">
              <History className="h-5 w-5 text-ink-500" strokeWidth={1.5} aria-hidden />
            </div>
            <p className="text-sm font-semibold text-ink-900">
              سجلّ النشاطات قيد الإعداد
            </p>
            <p className="max-w-md text-[12.5px] leading-relaxed text-ink-500">
              الأتمتة تعمل خلف الكواليس — تُنشر الإشارات إلى قسم مراجعة الفرص عند اجتيازها لبوّابة النشر.
            </p>
          </div>
        </div>
      </section>
    </AdminLayout>
  );
}
